import type { RiskPoint } from './riskChartSeries'

/**
 * Two-sided stacking for RiskStackedAreaChart. Per time, band k's positive edge is the running sum of the
 * positive parts of bands 0..k and its negative edge the running sum of the negative parts. Each side only grows
 * outward, so drawing each side outermost-first with opaque fills leaves every band a visible slice equal to its
 * own value (|v|), and the two outer edges add up to the net total. A single running total cannot do this: a
 * negative band pulls the total back inside an earlier band and that band's fill paints over it.
 * A band missing a time contributes 0 there; a band's points are emitted only at its own times.
 */
export function stackBands(bands: { id: string; data: RiskPoint[] }[]): { pos: RiskPoint[][]; neg: RiskPoint[][] } {
  const posTotal = new Map<string, number>()
  const negTotal = new Map<string, number>()
  const pos: RiskPoint[][] = []
  const neg: RiskPoint[][] = []
  for (const b of bands) {
    const p: RiskPoint[] = []
    const n: RiskPoint[] = []
    for (const pt of b.data) {
      const up = (posTotal.get(pt.time) ?? 0) + Math.max(0, pt.value)
      const down = (negTotal.get(pt.time) ?? 0) + Math.min(0, pt.value)
      posTotal.set(pt.time, up)
      negTotal.set(pt.time, down)
      p.push({ time: pt.time, value: up })
      n.push({ time: pt.time, value: down })
    }
    pos.push(p)
    neg.push(n)
  }
  return { pos, neg }
}
